/**
 * Utility functions for fetching event configuration files
 */

const S3_BASE_URL = "https://thoth-weblibrary.s3.ap-southeast-1.amazonaws.com";
const LOCAL_BASE_PATH = "/local-library";

/**
 * Get the appropriate URL for an event configuration file
 * In development mode (NODE_ENV === "dev"), uses local-library directory
 * In production, uses S3 bucket
 *
 * @param eventName - The event name/identifier
 * @param fileName - The configuration file name (e.g., 'main.json', 'talk_descriptions.json', 'hero-section.json')
 * @param cacheBust - Whether to add a cache-busting timestamp query parameter (default: false)
 * @returns The full URL/path to the configuration file
 */
export function getEventConfigUrl(
  eventName: string,
  fileName: string,
  cacheBust = false
): string {
  const baseUrl =
    process.env.VUE_APP_DEV === "true"
      ? `${LOCAL_BASE_PATH}/${eventName}/${fileName}`
      : `${S3_BASE_URL}/${eventName}/${fileName}`;

  if (cacheBust) {
    const timestamp = new Date().getTime();
    return `${baseUrl}?v=${timestamp}`;
  }

  return baseUrl;
}

/**
 * Fetch an event configuration file
 *
 * @param eventName - The event name/identifier
 * @param fileName - The configuration file name
 * @returns Promise with the parsed JSON data
 */
export async function fetchEventConfig(
  eventName: string,
  fileName: string
): Promise<any> {
  const url = getEventConfigUrl(eventName, fileName);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${fileName} for event ${eventName}: ${response.status}`
    );
  }

  return response.json();
}
